// Considered: AdditionalPropertiesGrid alone — rejected because a section group also mounts whole-aspect
// renderers and a slot, and both SpherePropertiesMolecule and the Sphere Editor's docked section tabs
// need that same body. See spec Reuse Audit, "New components — justified".
import React from 'react';
import { useTranslation } from 'localization';
import { useFormikContext } from 'formik';
import type { Schema, SchemaProperty } from 'types/schema';
import type { RenderMode } from 'types/aspect';
import AdditionalPropertiesGrid from './AdditionalPropertiesGrid';
import WholeAspectRenderer from './WholeAspectRenderer';
import { groupPropertiesByAspect } from '../../../utils/groupPropertiesByAspect';
import type { SphereSectionSlot, SphereSectionSlotContext } from '../../organisms/SphereEditor/sphereSectionSlots';
import './SectionGroupBodyMolecule.scss';

interface SectionGroupBodyMoleculeProps {
  properties: SchemaProperty[];
  schemaType: Schema;
  editable: boolean;
  writeBlockedProps?: Set<string>;
  onDockedPropertyEdit?: (propertyName: string) => void;
  slot?: SphereSectionSlot;
  slotContext?: SphereSectionSlotContext;
  /** Render legacy `dock: true` properties with their full editor instead of a display row. */
  renderDockedInline?: boolean;
}

/**
 * The content of one section group: its slot, then whole-aspect renderers and flattened property
 * rows in schema order (EAINT-855 review pass 2, M-8).
 */
const SectionGroupBodyMolecule: React.FC<SectionGroupBodyMoleculeProps> = ({
  properties,
  schemaType,
  editable,
  writeBlockedProps,
  onDockedPropertyEdit,
  slot,
  slotContext,
  renderDockedInline,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<{ properties: Record<string, unknown> }>();
  const mode: RenderMode = editable ? 'edit' : 'view';
  const { segments } = groupPropertiesByAspect(properties, schemaType.aspectComponents, mode, values.properties ?? {}, writeBlockedProps);
  const SlotComponent = slot?.Component;

  return (
    <>
      {SlotComponent && slotContext && <SlotComponent {...slotContext} />}
      {segments.map((segment, index) =>
        segment.type === 'group' ? (
          <div className="additional-properties-grid" key={segment.group.aspectSystemName}>
            <WholeAspectRenderer
              aspectSystemName={segment.group.aspectSystemName}
              ref_={segment.group.ref}
              properties={segment.group.properties}
              values={values.properties ?? {}}
              mode={segment.group.mode}
              onChange={(propertyName, value) => setFieldValue(`properties.${propertyName}`, value)}
              writeBlockedProps={writeBlockedProps}
              editable={editable}
              onDockedPropertyEdit={onDockedPropertyEdit}
            />
          </div>
        ) : (
          <div
            className="additional-properties-grid"
            key={`flat-${index}-${segment.properties[0]?.name ?? ''}`}
            // Wires the CSS ::before eyebrow when every property in this segment is
            // write-blocked (i.e. the segment is system fields).
            data-section-label={
              segment.properties.length > 0 && segment.properties.every((p) => writeBlockedProps?.has(p.name))
                ? t('sphere.property.systemFields', 'System fields')
                : undefined
            }
          >
            <AdditionalPropertiesGrid
              properties={segment.properties}
              editable={editable}
              onDockedPropertyEdit={onDockedPropertyEdit}
              writeBlockedProps={writeBlockedProps}
              renderDockedInline={renderDockedInline}
            />
          </div>
        )
      )}
    </>
  );
};

export default SectionGroupBodyMolecule;
